import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ChevronDown, X } from 'lucide-react';
import { SEL_API } from '../../lib/selectionShare';
import { CATEGORIES, MARKETS, MANUAL_PREFIX } from './marketCatalog';
import { Outcomes, PlayerOutcomes } from './MarketOutcomes';

const MODES = [['semplice', 'Semplice'], ['combo', 'Combo'], ['mix', 'Chance Mix']];
const modeBtn = (on) => `flex-1 py-2 rounded-xl text-xs font-black border transition-colors ${on ? 'bg-[#F5A623] text-[#1A1A1A] border-[#F5A623]' : 'bg-[#111] text-gray-300 border-white/10'}`;
const isDv = (m) => m.startsWith(MANUAL_PREFIX);
const KEEP_O = /segna o (?:colpisce )?palo|primo o ultimo|gol o assist o cartellino|gol o assist|gol o cartellino|cartellino o assist| o (?:il )?(?:suo )?sostituto/gi;
// an outcome that is already a combo can't go in a Chance Mix (and vice versa): the grouping would change its meaning
const clashes = (m, mode) => (mode === 'mix' ? m.includes(' + ') : mode === 'combo' && m.replace(KEEP_O, '').includes(' o '));

// Outcomes joined: Combo "A + B" (all must win), Chance Mix "A o B" (one is enough); "Da verificare" parts go last
export const joinParts = (parts, mode) => {
  const sorted = [...parts].sort((a, b) => isDv(a.m) - isDv(b.m));
  return sorted.map((p) => p.m).join(mode === 'mix' ? ' o ' : ' + ');
};

const OtherMarket = ({ onPick, onAdd, mode }) => {
  const [text, setText] = useState('');
  return (
    <div className="pb-3" data-testid="market-panel-other">
      <p className="text-xs text-gray-400 mb-2">Scrivi mercato ed esito come sulla schedina. L'uscita sarà salvata come "Da verificare".</p>
      <input value={text} onChange={(e) => setText(e.target.value)} maxLength={60} placeholder="es. Gol da calcio d'angolo Sì" className="w-full mb-2 bg-[#111] border border-white/10 rounded-xl px-3 py-2 text-white text-sm outline-none" data-testid="market-panel-other-input" />
      <button type="button" disabled={!text.trim()} onClick={() => (mode === 'semplice' ? onPick(`${MANUAL_PREFIX}${text.trim()}`) : onAdd({ id: 'altro', l: `Altro: ${text.trim()}`, m: `${MANUAL_PREFIX}${text.trim()}` }))} className="w-full py-2.5 rounded-xl bg-[#F5A623] text-[#1A1A1A] font-black disabled:opacity-40" data-testid="market-panel-other-confirm">Conferma</button>
    </div>
  );
};

const MarketRow = ({ market, open, onToggle, picked, children }) => (
  <div className="border-b border-white/5">
    <button type="button" onClick={onToggle} className="w-full flex items-center justify-between gap-2 py-2.5 text-left" data-testid={`market-row-${market.id}`}>
      <span className="text-sm text-white font-bold min-w-0">
        {market.name}{market.dv && <span className="ml-1.5 text-[9px] text-gray-500 font-black">DA VERIFICARE</span>}
        {picked && <span className="ml-1.5 inline-block px-2 py-0.5 rounded-full bg-[#F5A623] text-[#1A1A1A] text-[10px] font-black align-middle" data-testid={`market-row-picked-${market.id}`}>· {picked}</span>}
      </span>
      <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`} />
    </button>
    {open && children}
  </div>
);

// The bet being built, written out in full, with an X on each outcome
const Slip = ({ parts, mode, onRemove }) => (
  <div className="mb-3 rounded-2xl border-2 border-[#F5A623] bg-[#F5A623]/10 p-3" data-testid="market-panel-selection-text">
    <p className="text-[10px] font-black uppercase tracking-wider text-[#F5A623] mb-1.5">La tua giocata</p>
    <div className="flex flex-wrap items-center gap-1.5">
      {parts.map((p, i) => (
        <React.Fragment key={`${p.id}|${p.m}`}>
          {i > 0 && <span className="text-[#F5A623] font-black text-sm">{mode === 'mix' ? 'o' : '+'}</span>}
          <span className="inline-flex items-center gap-1 pl-2.5 pr-1 py-1 rounded-full bg-[#F5A623] text-[#1A1A1A] text-xs font-black" data-testid={`market-panel-slip-part-${i}`}>
            {p.s || p.l}
            <button type="button" onClick={() => onRemove(p)} className="p-0.5 rounded-full hover:bg-black/10" aria-label="Togli" data-testid={`market-panel-slip-remove-${i}`}><X className="w-3 h-3" /></button>
          </span>
        </React.Fragment>
      ))}
    </div>
  </div>
);

// Bottom sheet for one match: modes, search, markets by category (closed), outcomes on tap
export const MarketPanel = ({ match, current, onPick, onClose }) => {
  const [mode, setMode] = useState('semplice');
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(null);
  const [parts, setParts] = useState([]);
  const term = q.trim().toLowerCase();
  const list = useMemo(() => MARKETS.filter((m) => !term || m.name.toLowerCase().includes(term) || m.cat.toLowerCase().includes(term)), [term]);
  const isOn = (id, m, player) => parts.some((p) => p.id === id && (m == null || p.m === m) && (!player || p.m.includes(player)));

  const add = async (part) => {
    if (mode === 'semplice') { setParts([part]); return; }
    if (clashes(part.m, mode)) { toast.error(mode === 'mix' ? 'Questo esito è già una combo: non si può unire in Chance Mix' : 'Questo esito è già un Chance Mix: non si può unire in Combo'); return; }
    const lock = mode === 'combo' && part.player && parts.find((p) => p.player && p.player !== part.player);
    if (lock) { toast.error(`In Combo i mercati giocatore devono essere dello stesso giocatore (${lock.player})`); return; }
    // one outcome per market; Chance Mix allows the same player market with different players
    const next = [...parts.filter((p) => p.id !== part.id || (mode === 'mix' && part.player && p.player && p.player !== part.player)), part];
    if (mode === 'combo' && next.length > 1) {
      const { data } = await axios.post(`${SEL_API}/market-check`, { market: joinParts(next, 'combo') }).catch(() => ({ data: { possible: true } }));
      if (!data.possible) { toast.error('Questi esiti non possono vincere insieme'); return; }
    }
    setParts(next);
  };
  const lockPlayer = mode === 'combo' ? parts.find((p) => p.player)?.player : null;
  const pickedOf = (id) => parts.filter((p) => p.id === id).map((p) => p.o).join(', ');
  const toCombo = (ps) => {
    const pl = ps.find((p) => p.player)?.player;
    return ps.filter((p, i) => (!p.player || p.player === pl) && ps.findIndex((x) => x.id === p.id) === i);
  };
  const switchMode = (m) => {
    setMode(m);
    const kept = m === 'semplice' ? parts.slice(-1) : parts.filter((p) => !clashes(p.m, m));
    const next = m === 'combo' ? toCombo(kept) : kept;
    if (next.length < parts.length) toast('Alcuni esiti sono stati tolti perché non validi in questa modalità');
    setParts(next);
  };

  return (
    <div className="fixed inset-0 z-[9995] bg-black/70 flex items-end sm:items-center justify-center" data-testid="market-panel">
      <div className="w-full max-w-md bg-[#1A1A1A] rounded-t-3xl sm:rounded-3xl border border-white/10 p-5 max-h-[88dvh] pb-[calc(1rem+max(env(safe-area-inset-bottom,0px),3rem))] sm:pb-5 flex flex-col">
        <div className="flex items-start justify-between gap-2 mb-3">
          <div className="min-w-0">
            <p className="text-white font-black truncate">{match.home_team} - {match.away_team}</p>
            <p className="text-xs text-gray-500 truncate" data-testid="market-panel-current">{current ? `Attuale: ${current}` : 'Scegli mercato ed esito'}</p>
          </div>
          <button type="button" onClick={onClose} className="p-1 text-gray-400" data-testid="market-panel-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex gap-1.5 mb-3">
          {MODES.map(([k, l]) => <button key={k} type="button" onClick={() => switchMode(k)} className={modeBtn(mode === k)} data-testid={`market-mode-${k}`}>{l}</button>)}
        </div>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca mercato" className="w-full mb-2 bg-[#111] border border-white/10 rounded-xl px-3 py-2 text-white text-sm outline-none" data-testid="market-panel-search" />
        <div className="flex-1 overflow-y-auto pr-1" data-testid="market-panel-list">
          {CATEGORIES.map((cat) => {
            const ms = list.filter((m) => m.cat === cat);
            if (!ms.length) return null;
            return (
              <div key={cat} className="mb-2">
                <p className="text-[10px] font-black uppercase tracking-wider text-[#F5A623] mt-2">{cat}</p>
                {ms.map((m) => (
                  <MarketRow key={m.id} market={m} open={open === m.id} picked={pickedOf(m.id)} onToggle={() => setOpen(open === m.id ? null : m.id)}>
                    {m.player ? <PlayerOutcomes market={m} fixtureId={match.fixture_id} isOn={isOn} onPick={add} lockPlayer={lockPlayer} /> : <Outcomes market={m} isOn={isOn} onPick={add} />}
                  </MarketRow>
                ))}
              </div>
            );
          })}
          {(!term || 'altro'.includes(term)) && (
            <MarketRow market={{ id: 'altro', name: 'Altro' }} open={open === 'altro'} onToggle={() => setOpen(open === 'altro' ? null : 'altro')}>
              <OtherMarket onPick={onPick} onAdd={add} mode={mode} />
            </MarketRow>
          )}
        </div>
        {parts.length > 0 && (
          <div className="pt-3 border-t border-white/10" data-testid="market-panel-selection">
            <Slip parts={parts} mode={mode} onRemove={(x) => setParts(parts.filter((p) => p !== x))} />
            <div className="flex gap-2">
              <button type="button" onClick={() => setParts([])} className="px-4 py-2.5 rounded-xl bg-[#242424] text-gray-300 text-sm font-bold" data-testid="market-panel-clear">Svuota</button>
              <button type="button" onClick={() => onPick(mode === 'semplice' ? parts[0].m : joinParts(parts, mode))} className="flex-1 py-2.5 rounded-xl bg-[#F5A623] text-[#1A1A1A] font-black" data-testid="market-panel-confirm">Conferma</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MarketPanel;
